import { useTranslation } from "react-i18next"

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"

type ConsumerMode = "off" | "shadow" | "primary"

type Props = {
  consumerMode: ConsumerMode
  delegatedScope: string
  companyWide: boolean
  onChange: (patch: {
    consumer_mode?: ConsumerMode
    delegated_scope?: string
    company_wide?: boolean
  }) => void
}

export function FilthContextModeFields({
  consumerMode,
  delegatedScope,
  companyWide,
  onChange,
}: Props) {
  const { t } = useTranslation()

  return (
    <>
      <div className="grid gap-3">
        <Label htmlFor="filth-consumer-mode">
          {t("adminDashboard.filthBackends.field.consumerMode", "Hello AI retrieval mode")}
        </Label>
        <Select
          value={consumerMode}
          onValueChange={(value) => onChange({ consumer_mode: value as ConsumerMode })}
        >
          <SelectTrigger className="min-h-10" id="filth-consumer-mode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="off">
              {t("adminDashboard.filthBackends.mode.off", "Legacy")}
            </SelectItem>
            <SelectItem value="shadow">
              {t("adminDashboard.filthBackends.mode.shadow", "Shadow validation")}
            </SelectItem>
            <SelectItem value="primary">
              {t("adminDashboard.filthBackends.mode.primary", "Context v1")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      {consumerMode !== "off" && (
        <div className="grid gap-3">
          <Label htmlFor="filth-delegated-scope">
            {t("adminDashboard.filthBackends.field.delegatedScope", "Entra delegated scope")}
          </Label>
          <Input
            className="min-h-10"
            id="filth-delegated-scope"
            placeholder="api://filth/access_as_user"
            value={delegatedScope}
            onChange={(event) => onChange({ delegated_scope: event.target.value })}
          />
        </div>
      )}
      {consumerMode === "primary" ? (
        <div className="space-y-2 rounded-lg border p-4">
          <div className="text-sm font-medium">
            {t("adminDashboard.filthBackends.field.perUser", "Per-user knowledge spaces")}
          </div>
          <div className="text-muted-foreground max-w-prose text-sm leading-relaxed">
            {t(
              "adminDashboard.filthBackends.field.perUserHint",
              "Prism filters company, team, personal, and source access for the signed-in user on every request."
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="min-w-0 space-y-2 pr-4">
            <div className="text-sm font-medium">
              {t("adminDashboard.filthBackends.field.companyWide", "Company-wide content")}
            </div>
            <div className="text-muted-foreground max-w-prose text-sm leading-relaxed">
              {t(
                "adminDashboard.filthBackends.field.companyWideHint",
                "Legacy content must be readable by everyone in the tenant because it has no per-document ACL."
              )}
            </div>
          </div>
          <Switch
            checked={companyWide}
            className="shrink-0"
            onCheckedChange={(checked) => onChange({ company_wide: checked })}
          />
        </div>
      )}
    </>
  )
}
